import React, { useState, useEffect } from "react";
import { testsuiteApi } from "../../api/testsuite";

const ScheduleTestModal = ({ isOpen, onClose, suiteId, suiteName }) => {
  const [testCases, setTestCases] = useState([]);
  const [selectedTestCases, setSelectedTestCases] = useState({});
  const [initialLoading, setInitialLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Existing scheduled job state
  const [existingScheduledJob, setExistingScheduledJob] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Schedule form state
  const [scheduleName, setScheduleName] = useState("");
  const [description, setDescription] = useState("");
  const [frequency, setFrequency] = useState("daily"); // 'daily' or 'weekly'
  const [timeHours, setTimeHours] = useState("09");
  const [timeMinutes, setTimeMinutes] = useState("00");
  const [timeAmPm, setTimeAmPm] = useState("AM");
  const [timezone, setTimezone] = useState("UTC");
  const [isActive, setIsActive] = useState(true);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [selectedDay, setSelectedDay] = useState("1"); // Monday by default

  // Frequency options
  const frequencyOptions = [
    { label: "Daily", value: "daily" },
    { label: "Weekly", value: "weekly" },
  ];

  // Day options for weekly frequency
  const dayOptions = [
    { label: "Monday", value: "1" },
    { label: "Tuesday", value: "2" },
    { label: "Wednesday", value: "3" },
    { label: "Thursday", value: "4" },
    { label: "Friday", value: "5" },
    { label: "Saturday", value: "6" },
    { label: "Sunday", value: "0" },
  ];

  // Time options
  const hourOptions = Array.from({ length: 12 }, (_, i) =>
    (i + 1).toString().padStart(2, "0")
  );
  const minuteOptions = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, "0")
  );
  const amPmOptions = ["AM", "PM"];

  useEffect(() => {
    if (isOpen && suiteId) {
      setInitialLoading(true);
      setError(null);
      Promise.all([fetchTestCases(), checkExistingScheduledJob()]).finally(
        () => {
          setInitialLoading(false);
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, suiteId, suiteName]);

  // Set default test case selection for new schedules after test cases are loaded
  useEffect(() => {
    if (
      testCases.length > 0 &&
      !isEditing &&
      Object.keys(selectedTestCases).length === 0
    ) {
      // Select all enabled test cases by default for new schedules
      const allSelected = {};
      testCases.forEach((tc) => {
        if (!tc.is_disabled) {
          allSelected[tc.id] = true;
        }
      });
      setSelectedTestCases(allSelected);
    }
  }, [testCases, isEditing]);

  const checkExistingScheduledJob = async () => {
    try {
      // First, get test cases for this suite to know which test case IDs belong to it
      const testCasesResponse = await testsuiteApi.getTestCasesForScheduling(
        suiteId
      );
      const suiteTestCaseIds = (testCasesResponse.test_cases || []).map(
        (tc) => tc.id
      );

      // Get all scheduled jobs for the user
      const scheduledJobs = await testsuiteApi.getScheduledJobs();

      // Find a job that includes test cases from this suite
      const existingJob = scheduledJobs.find((job) => {
        if (job.test_selection?.type === "test_cases") {
          const jobTestCaseIds = job.test_selection.test_case_ids || [];
          // Check if any test case from this job belongs to the current suite
          return jobTestCaseIds.some((id) => suiteTestCaseIds.includes(id));
        } else if (job.test_selection?.type === "test_suites") {
          return job.test_selection.test_suite_ids?.includes(suiteId);
        }
        return false;
      });

      if (existingJob) {
        setExistingScheduledJob(existingJob);
        setIsEditing(true);
        loadExistingJobData(existingJob);
      } else {
        setScheduleName(`Scheduled run for ${suiteName}`);
      }
    } catch (error) {
      console.error("Error checking existing scheduled job:", error);
      setScheduleName(`Scheduled run for ${suiteName}`);
    }
  };

  const loadExistingJobData = (job) => {
    setScheduleName(job.name);
    setDescription(job.description || "");
    setTimezone(job.timezone);
    setIsActive(job.is_active);

    // Parse existing cron schedule to extract frequency and time
    parseCronToFormFields(job.cron_schedule);

    // Load selected test cases if this is a test_cases selection
    if (job.test_selection?.type === "test_cases") {
      const selectedIds = job.test_selection.test_case_ids || [];
      const selectedMap = {};
      selectedIds.forEach((id) => {
        selectedMap[id] = true;
      });
      setSelectedTestCases(selectedMap);
    }
  };

  const parseCronToFormFields = (cronSchedule) => {
    // Parse cron schedule like "0 9 * * *" or "0 9 * * 1"
    const parts = cronSchedule.split(" ");
    if (parts.length >= 5) {
      const minute = parts[0];
      const hour = parts[1];
      const dayOfWeek = parts[4];

      // Convert 24-hour to 12-hour format
      let hour12 = parseInt(hour);
      let ampm = "AM";

      if (hour12 === 0) {
        hour12 = 12;
      } else if (hour12 > 12) {
        hour12 = hour12 - 12;
        ampm = "PM";
      } else if (hour12 === 12) {
        ampm = "PM";
      }

      setTimeHours(hour12.toString().padStart(2, "0"));
      setTimeMinutes(minute.padStart(2, "0"));
      setTimeAmPm(ampm);

      // Determine frequency based on day of week
      if (dayOfWeek === "*") {
        setFrequency("daily");
      } else {
        setFrequency("weekly");
        setSelectedDay(dayOfWeek);
      }
    }
  };

  const generateCronSchedule = () => {
    // Convert 12-hour format to 24-hour
    let hour24 = parseInt(timeHours);
    if (timeAmPm === "PM" && hour24 !== 12) {
      hour24 += 12;
    } else if (timeAmPm === "AM" && hour24 === 12) {
      hour24 = 0;
    }

    const minute = timeMinutes;
    const hour = hour24.toString();

    // Generate cron based on frequency
    if (frequency === "daily") {
      return `${minute} ${hour} * * *`;
    } else {
      // Weekly - run on selected day
      return `${minute} ${hour} * * ${selectedDay}`;
    }
  };

  const handleMinuteChange = (newValue) => {
    let minute = parseInt(newValue);
    if (isNaN(minute)) {
      minute = 0;
    } else if (minute < 0) {
      minute = 59;
    } else if (minute > 59) {
      minute = 0;
    }
    setTimeMinutes(minute.toString().padStart(2, "0"));
  };

  const handleMinuteInputChange = (value) => {
    // Allow empty string for deletion
    if (value === "") {
      setTimeMinutes("");
      return;
    }

    // Only allow numbers
    if (!/^\d+$/.test(value)) {
      return;
    }

    let minute = parseInt(value);

    // For 2-digit numbers, check if they're valid minutes
    if (value.length === 2) {
      if (minute >= 0 && minute <= 59) {
        setTimeMinutes(value);
      }
      // If invalid (like 84), don't update the state
      return;
    }

    // For single digit, allow 0-9
    if (value.length === 1 && minute >= 0 && minute <= 9) {
      setTimeMinutes(value);
    }
  };

  const handleHourChange = (newValue) => {
    let hour = parseInt(newValue);
    if (isNaN(hour)) {
      hour = 1;
    } else if (hour < 1) {
      hour = 12;
    } else if (hour > 12) {
      hour = 1;
    }
    setTimeHours(hour.toString().padStart(2, "0"));
  };

  const handleHourInputChange = (value) => {
    // Allow empty string for deletion
    if (value === "") {
      setTimeHours("");
      return;
    }

    // Only allow numbers
    if (!/^\d+$/.test(value)) {
      return;
    }

    let hour = parseInt(value);

    // For 2-digit numbers, check if they're valid hours
    if (value.length === 2) {
      if (hour >= 1 && hour <= 12) {
        setTimeHours(value);
      }
      // If invalid (like 13), don't update the state
      return;
    }

    // For single digit, allow 1-9
    if (value.length === 1 && hour >= 1 && hour <= 9) {
      setTimeHours(value);
    }
  };

  const fetchTestCases = async () => {
    try {
      setError(null);
      const response = await testsuiteApi.getTestCasesForScheduling(suiteId);
      setTestCases(response.test_cases || []);

      // Don't auto-select test cases here - let checkExistingScheduledJob handle it
      // For new schedules, we'll set selection after checking for existing jobs
    } catch (error) {
      console.error("Error fetching test cases:", error);
      setError("Failed to load test cases: " + error.message);
    }
  };

  const toggleTestCase = (testCaseId) => {
    setSelectedTestCases((prev) => ({
      ...prev,
      [testCaseId]: !prev[testCaseId],
    }));
  };

  const toggleSelectAll = () => {
    const enabledTestCases = testCases.filter((tc) => !tc.is_disabled);
    const allSelected = enabledTestCases.every(
      (tc) => selectedTestCases[tc.id]
    );

    if (allSelected) {
      // Deselect all
      setSelectedTestCases({});
    } else {
      // Select all enabled test cases
      const newSelection = {};
      enabledTestCases.forEach((tc) => {
        newSelection[tc.id] = true;
      });
      setSelectedTestCases(newSelection);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const selectedIds = Object.keys(selectedTestCases).filter(
      (id) => selectedTestCases[id]
    );

    if (testCases.length === 0) {
      setError(
        "No test cases found in this test suite. Please add test cases before scheduling."
      );
      return;
    }

    if (selectedIds.length === 0) {
      setError("Please select at least one test case");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      // Generate cron schedule from form fields
      const cronSchedule = generateCronSchedule();

      if (!cronSchedule) {
        setError("Please specify a valid schedule");
        return;
      }

      const scheduleData = {
        name: scheduleName,
        description,
        cron_schedule: cronSchedule,
        timezone,
        test_selection: {
          type: "test_cases",
          test_case_ids: selectedIds,
        },
        is_active: isActive,
      };

      if (isEditing && existingScheduledJob) {
        // Update existing scheduled job
        await testsuiteApi.updateScheduledJob(
          existingScheduledJob.id,
          scheduleData
        );
        alert("Scheduled job updated successfully!");
      } else {
        // Create new scheduled job
        await testsuiteApi.createScheduledJob(scheduleData);
        alert("Test suite scheduled successfully!");
      }

      // Reset form and close modal
      resetForm();
      onClose();
    } catch (error) {
      console.error("Error saving scheduled job:", error);
      setError("Failed to save schedule: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSchedule = async () => {
    if (!existingScheduledJob) return;

    if (
      !window.confirm(
        `Are you sure you want to delete the schedule "${existingScheduledJob.name}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await testsuiteApi.deleteScheduledJob(existingScheduledJob.id);

      alert("Scheduled job deleted successfully!");
      resetForm();
      onClose();
    } catch (error) {
      console.error("Error deleting scheduled job:", error);
      setError("Failed to delete schedule: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setScheduleName("");
    setDescription("");
    setFrequency("daily");
    setTimeHours("09");
    setTimeMinutes("00");
    setTimeAmPm("AM");
    setTimezone("UTC");
    setIsActive(true);
    setSelectedTestCases({});
    setError(null);
    setExistingScheduledJob(null);
    setIsEditing(false);
    setInitialLoading(false);
    setShowTimePicker(false);
    setSelectedDay("1"); // Reset selected day
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getScheduleDescription = () => {
    const timeStr = `${timeHours.padStart(2, "0")}:${timeMinutes.padStart(
      2,
      "0"
    )} ${timeAmPm}`;
    if (frequency === "daily") {
      return `Runs daily at ${timeStr} ${timezone}`;
    } else {
      return `Runs every ${
        dayOptions.find((day) => day.value === selectedDay)?.label
      } at ${timeStr} ${timezone}`;
    }
  };

  // Close time picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTimePicker && !event.target.closest(".time-picker-container")) {
        setShowTimePicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showTimePicker]);

  if (!isOpen) return null;

  const selectedCount = Object.values(selectedTestCases).filter(Boolean).length;
  const enabledTestCases = testCases.filter((tc) => !tc.is_disabled);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {isEditing ? "Edit Schedule:" : "Schedule Test Suite:"}{" "}
                {suiteName}
              </h3>
              {isEditing && existingScheduledJob && (
                <p className="text-sm text-blue-600 mt-1">
                  Editing existing schedule: "{existingScheduledJob.name}"
                  {existingScheduledJob.last_run_at && (
                    <span className="block text-xs text-gray-500">
                      Last run:{" "}
                      {new Date(
                        existingScheduledJob.last_run_at
                      ).toLocaleString()}
                    </span>
                  )}
                  {existingScheduledJob.next_run_at && (
                    <span className="block text-xs text-gray-500">
                      Next run:{" "}
                      {new Date(
                        existingScheduledJob.next_run_at
                      ).toLocaleString()}
                    </span>
                  )}
                </p>
              )}
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {initialLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading schedule data...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Schedule Details */}
              <div className="border-b pb-6">
                <h4 className="text-md font-medium text-gray-900 mb-4">
                  Schedule Details
                </h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Schedule Name *
                    </label>
                    <input
                      type="text"
                      value={scheduleName}
                      onChange={(e) => setScheduleName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Frequency *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <select
                        value={frequency}
                        onChange={(e) => setFrequency(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                        required
                      >
                        {frequencyOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Time *
                    </label>
                    <div className="relative time-picker-container">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <input
                        type="text"
                        value={`${timeHours.padStart(
                          2,
                          "0"
                        )}:${timeMinutes.padStart(2, "0")} ${timeAmPm}`}
                        readOnly
                        className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                        onClick={() => setShowTimePicker(!showTimePicker)}
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                    </div>

                    {/* Time Picker Popup */}
                    {showTimePicker && (
                      <div className="absolute z-10 mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3 time-picker-container">
                        <div className="flex items-center space-x-2 mb-3">
                          <div className="flex flex-col items-center">
                            <label className="text-xs text-gray-500 mb-1">
                              Hour
                            </label>
                            <div className="flex items-center border border-gray-300 rounded">
                              <button
                                type="button"
                                onClick={() =>
                                  handleHourChange(
                                    parseInt(timeHours || "1") + 1
                                  )
                                }
                                className="px-2 py-1 hover:bg-gray-100 text-gray-600"
                              >
                                ▲
                              </button>
                              <input
                                type="text"
                                value={timeHours}
                                onChange={(e) =>
                                  handleHourInputChange(e.target.value)
                                }
                                onBlur={() => {
                                  const hour = parseInt(timeHours || "1");
                                  handleHourChange(hour);
                                }}
                                className="w-8 px-1 py-1 border-0 text-center text-sm focus:outline-none"
                                maxLength={2}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  handleHourChange(
                                    parseInt(timeHours || "1") - 1
                                  )
                                }
                                className="px-2 py-1 hover:bg-gray-100 text-gray-600"
                              >
                                ▼
                              </button>
                            </div>
                          </div>

                          <span className="text-gray-500 text-lg font-bold">
                            :
                          </span>

                          <div className="flex flex-col items-center">
                            <label className="text-xs text-gray-500 mb-1">
                              Minute
                            </label>
                            <div className="flex items-center border border-gray-300 rounded">
                              <button
                                type="button"
                                onClick={() =>
                                  handleMinuteChange(
                                    parseInt(timeMinutes || "0") + 1
                                  )
                                }
                                className="px-2 py-1 hover:bg-gray-100 text-gray-600"
                              >
                                ▲
                              </button>
                              <input
                                type="text"
                                value={timeMinutes}
                                onChange={(e) =>
                                  handleMinuteInputChange(e.target.value)
                                }
                                onBlur={() => {
                                  const minute = parseInt(timeMinutes || "0");
                                  handleMinuteChange(minute);
                                }}
                                className="w-8 px-1 py-1 border-0 text-center text-sm focus:outline-none"
                                maxLength={2}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  handleMinuteChange(
                                    parseInt(timeMinutes || "0") - 1
                                  )
                                }
                                className="px-2 py-1 hover:bg-gray-100 text-gray-600"
                              >
                                ▼
                              </button>
                            </div>
                          </div>

                          <div className="flex flex-col items-center">
                            <label className="text-xs text-gray-500 mb-1">
                              AM/PM
                            </label>
                            <select
                              value={timeAmPm}
                              onChange={(e) => setTimeAmPm(e.target.value)}
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              {amPmOptions.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {frequency === "weekly" && (
                          <div className="mb-3">
                            <label className="text-xs text-gray-500 mb-1 block">
                              Day of Week
                            </label>
                            <select
                              value={selectedDay}
                              onChange={(e) => setSelectedDay(e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                            >
                              {dayOptions.map((day) => (
                                <option key={day.value} value={day.value}>
                                  {day.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <button
                          type="button"
                          onClick={() => setShowTimePicker(false)}
                          className="w-full px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                        >
                          Done
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Timezone *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      <select
                        value={timezone}
                        onChange={(e) => setTimezone(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 appearance-none"
                      >
                        <option value="UTC">UTC</option>
                        <option value="America/New_York">Eastern Time</option>
                        <option value="America/Chicago">Central Time</option>
                        <option value="America/Denver">Mountain Time</option>
                        <option value="America/Los_Angeles">
                          Pacific Time
                        </option>
                        <option value="Europe/London">London</option>
                        <option value="Europe/Paris">Paris</option>
                        <option value="Asia/Tokyo">Tokyo</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg
                          className="w-5 h-5 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Optional description for this scheduled job"
                    />
                  </div>
                </div>

                {/* Schedule Status */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="text-sm font-medium text-gray-900 mb-1">
                        Schedule Status
                      </h5>
                      <p className="text-xs text-gray-600">
                        {isActive
                          ? "This schedule is active and will run automatically"
                          : "This schedule is disabled and will not run"}
                      </p>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isActive"
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                        className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors"
                      />
                      <label
                        htmlFor="isActive"
                        className="ml-3 flex items-center cursor-pointer"
                      >
                        <span
                          className={`text-sm font-medium ${
                            isActive ? "text-green-700" : "text-gray-500"
                          }`}
                        >
                          {isActive ? "✓ Active" : "○ Inactive"}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Test Case Selection */}
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-md font-medium text-gray-900">
                    {testCases.length === 0
                      ? "No Test Cases Available"
                      : `Select Test Cases (${selectedCount} of ${enabledTestCases.length} selected)`}
                  </h4>
                  {enabledTestCases.length > 0 && (
                    <button
                      type="button"
                      onClick={toggleSelectAll}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {enabledTestCases.every((tc) => selectedTestCases[tc.id])
                        ? "Deselect All"
                        : "Select All"}
                    </button>
                  )}
                </div>

                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
                  {testCases.length === 0 ? (
                    <div className="p-4 text-center text-gray-500">
                      No test cases found in this test suite. Please add some
                      test cases to schedule a test run.
                    </div>
                  ) : (
                    testCases.map((testCase) => (
                      <div
                        key={testCase.id}
                        className={`flex items-center p-3 border-b border-gray-100 last:border-b-0 ${
                          testCase.is_disabled
                            ? "bg-gray-50 opacity-50"
                            : "hover:bg-gray-50"
                        }`}
                      >
                        <input
                          type="checkbox"
                          id={`test-${testCase.id}`}
                          checked={!!selectedTestCases[testCase.id]}
                          onChange={() =>
                            !testCase.is_disabled && toggleTestCase(testCase.id)
                          }
                          disabled={testCase.is_disabled}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={`test-${testCase.id}`}
                          className={`ml-3 flex-1 ${
                            testCase.is_disabled
                              ? "cursor-not-allowed"
                              : "cursor-pointer"
                          }`}
                        >
                          <div className="text-sm font-medium text-gray-900">
                            {testCase.testname}
                            {testCase.is_disabled && (
                              <span className="ml-2 text-xs text-gray-500">
                                (Disabled)
                              </span>
                            )}
                          </div>
                          {testCase.description && (
                            <div className="text-xs text-gray-500 mt-1">
                              {testCase.description}
                            </div>
                          )}
                        </label>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Generated Schedule */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                <h5 className="text-sm font-medium text-gray-900 mb-2">
                  Generated Schedule
                </h5>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">
                    Cron:{" "}
                    <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded-md">
                      {generateCronSchedule()}
                    </span>
                  </span>
                  <span className="text-xs text-gray-600">
                    {getScheduleDescription()}
                  </span>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-between items-center pt-4 border-t">
                <div>
                  {isEditing && existingScheduledJob && (
                    <button
                      type="button"
                      onClick={handleDeleteSchedule}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Delete Schedule
                    </button>
                  )}
                </div>
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      submitting ||
                      selectedCount === 0 ||
                      testCases.length === 0
                    }
                    className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                      submitting ||
                      selectedCount === 0 ||
                      testCases.length === 0
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-blue-600 hover:bg-blue-700"
                    }`}
                  >
                    {submitting ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {isEditing
                          ? "Updating Schedule..."
                          : "Creating Schedule..."}
                      </>
                    ) : testCases.length === 0 ? (
                      "No Test Cases Available"
                    ) : isEditing ? (
                      "Update Schedule"
                    ) : (
                      "Create Schedule"
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleTestModal;
